import React, { ElementType, ReactElement, useEffect, useRef, useState } from "react";
import { RevealProps } from "./types";

interface RevealImplProps {
  as?: ElementType;
  translate?: boolean;
  delayMs?: number;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

// Fades/slides content in the first time it scrolls into view. Polymorphic
// (`as`) so it renders as the actual target tag — no extra wrapper element —
// which matters for tags with placement rules (e.g. <li> must be a direct
// child of <ul>/<ol>) and keeps this a drop-in replacement at any call site.
// One-shot: once revealed, the observer disconnects and it stays visible.
//
// Implemented against a loose internal prop type and re-exported with the
// strict polymorphic signature below — TS can't verify arbitrary passthrough
// props against a generic, unconstrained `T` from inside the function body,
// so the type safety for callers lives entirely in the exported signature.
const RevealImpl: React.FC<RevealImplProps & Record<string, unknown>> = ({
  as,
  translate = true,
  delayMs = 0,
  className = "",
  style,
  children,
  ...rest
}) => {
  const Comp = (as ?? "div") as ElementType;
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    // A null ref here means `as` was set to something that doesn't forward
    // its ref to a DOM node (e.g. a plain function component like
    // gatsby-plugin-image's GatsbyImage) — fail open rather than leaving the
    // element stuck at opacity-0 forever with no observer to ever flip it.
    if (
      !el ||
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const hiddenState = translate ? "opacity-0 translate-y-6" : "opacity-0";
  const visibleState = translate ? "opacity-100 translate-y-0" : "opacity-100";

  return (
    <Comp
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? visibleState : hiddenState} ${className}`}
      style={delayMs ? { ...style, transitionDelay: `${delayMs}ms` } : style}
      {...rest}
    >
      {children}
    </Comp>
  );
};

const Reveal = RevealImpl as <T extends ElementType = "div">(props: RevealProps<T>) => ReactElement;

export default Reveal;
